/*
 * Maison Soya — ms-en-tete
 * ----------------------------------------------------------------------------
 * Trois mécaniques indépendantes, chacune inoffensive sans les autres :
 *
 *   1. L'état « défilé » — une sentinelle d'un pixel observée par
 *      IntersectionObserver, pas un écouteur `scroll`. Sa sortie du viewport
 *      pose .ms-en-tete--defile, qui repasse un en-tête transparent à l'opaque
 *      et ajoute l'ombre légère d'un en-tête déjà collant. Même idiome que
 *      ms-panier.js et ms-media-points.js.
 *   2. Le panneau de sous-menu — un bouton par entrée porte aria-expanded ;
 *      le survol l'ouvre en CSS pur (voir ms-en-tete.css), ce script ne gère
 *      que le clavier : Entrée/clic sur le bouton, Échap, et la fermeture à la
 *      sortie de focus.
 *   3. Le compteur de panier — écoute déléguée de `ms:panier:maj`
 *      (assets/ms-panier.js émet déjà cet événement) et, pour le retour
 *      arrière du navigateur, une relecture de l'URL portée par
 *      data-url au `pageshow` avec `event.persisted`.
 *
 * Le tiroir mobile et la modale de recherche n'ont rien ici : ce sont des
 * <dialog> ouverts par assets/ms-modale.js, déjà chargé par
 * snippets/ms-modale.liquid.
 */
if (!window.msEnTeteInitialisee) {
  window.msEnTeteInitialisee = true;

  document.querySelectorAll('.ms-en-tete').forEach((entete) => {
    entete.classList.add('ms-en-tete--js');
    /* Le collant vit sur le conteneur de section (voir la note dans
       sections/ms-en-tete.liquid) : c'est donc lui qui doit porter le témoin
       « le script est prêt », dont dépend le collant en mode transparent. */
    entete.closest('.shopify-section')?.classList.add('ms-en-tete-section--js');

    /* Hauteur réelle de la barre, publiée en variable CSS.
       ----------------------------------------------------------------------
       La proposition d'étape 2 avait écarté ce calcul, à raison : rien n'en
       avait besoin tant que le panneau de sous-menu se positionnait en
       `absolute` sous la barre. Le tiroir mobile, lui, est un <dialog> — il
       vit dans la couche supérieure, donc positionné par rapport au viewport
       et non au document. Pour qu'il s'arrête SOUS la barre au lieu de la
       recouvrir (comportement relevé sur la référence, corrigé le
       4 septembre 2026), il faut cette hauteur. Elle est mesurée, pas
       supposée : le logo est réglable, la barre n'a donc pas de hauteur fixe.
       ResizeObserver plutôt qu'un écouteur `resize` : la barre change aussi
       de hauteur quand une police d'icône charge ou qu'un bandeau apparaît. */
    const publierHauteur = () => {
      document.documentElement.style.setProperty(
        '--ms-en-tete-hauteur',
        `${entete.getBoundingClientRect().height}px`
      );
    };
    publierHauteur();
    if ('ResizeObserver' in window) new ResizeObserver(publierHauteur).observe(entete);

    const sentinelle = entete.querySelector('.ms-en-tete__sentinelle');
    if (!sentinelle || !('IntersectionObserver' in window)) return;

    const observateur = new IntersectionObserver(
      ([entree]) => {
        entete.classList.toggle('ms-en-tete--defile', !entree.isIntersecting);
      },
      { threshold: 0 }
    );
    observateur.observe(sentinelle);
  });

  const fermerEntree = (entree, restituerFocus) => {
    const declencheur = entree.querySelector('[data-ms-en-tete-declencheur]');
    entree.classList.remove('ms-en-tete__entree--ouverte');
    if (!declencheur) return;
    declencheur.setAttribute('aria-expanded', 'false');
    if (restituerFocus) declencheur.focus();
  };

  document.addEventListener('click', (evenement) => {
    const declencheur = evenement.target.closest('[data-ms-en-tete-declencheur]');
    if (!declencheur) return;
    const entree = declencheur.closest('.ms-en-tete__entree');
    if (!entree) return;

    const etaitOuverte = entree.classList.contains('ms-en-tete__entree--ouverte');

    /* Une seule entrée ouverte à la fois — celle qu'on vient de fermer par
       toggle n'a pas besoin de reprendre le focus, elle l'a déjà. */
    document.querySelectorAll('.ms-en-tete__entree--ouverte').forEach((autre) => {
      if (autre !== entree) fermerEntree(autre, false);
    });

    entree.classList.toggle('ms-en-tete__entree--ouverte', !etaitOuverte);
    declencheur.setAttribute('aria-expanded', String(!etaitOuverte));
  });

  document.addEventListener('keydown', (evenement) => {
    if (evenement.key !== 'Escape') return;
    const entree = document.querySelector('.ms-en-tete__entree--ouverte');
    if (entree) fermerEntree(entree, true);
  });

  /* Le focus quitte l'entrée (Tab hors du panneau, clic ailleurs sur la
     page) : referme, sans lui rendre le focus — il est déjà ailleurs. */
  document.addEventListener('focusin', (evenement) => {
    document.querySelectorAll('.ms-en-tete__entree--ouverte').forEach((entree) => {
      if (!entree.contains(evenement.target)) fermerEntree(entree, false);
    });
  });

  /* Un clic hors de toute entrée (souris, écran tactile) referme aussi — pas
     seulement la perte de focus au clavier ci-dessus. */
  document.addEventListener('click', (evenement) => {
    document.querySelectorAll('.ms-en-tete__entree--ouverte').forEach((entree) => {
      if (!entree.contains(evenement.target)) fermerEntree(entree, false);
    });
  });

  /* Le burger porte les deux états — ouvrir puis fermer le tiroir.
     ------------------------------------------------------------------------
     L'ouverture reste l'affaire de ms-modale.js (le lien porte data-ms-modale
     et garde sa destination de repli sans JavaScript). La FERMETURE ne peut
     pas passer par data-ms-modale-fermer : ce mécanisme ferme le <dialog>
     ancêtre du bouton, or ce bouton-ci vit dans la barre, hors du tiroir.
     On intercepte donc le clic AVANT ms-modale (écouteur en phase de capture)
     quand la boîte est déjà ouverte, sans quoi son showModal() lèverait une
     erreur sur un dialogue déjà ouvert. */
  document.addEventListener(
    'click',
    (evenement) => {
      const bouton = evenement.target.closest('[data-ms-en-tete-fermer-tiroir]');
      if (!bouton) return;
      const boite = document.getElementById(bouton.dataset.msEnTeteFermerTiroir);
      if (!boite || !boite.open) return;
      evenement.preventDefault();
      evenement.stopPropagation();
      boite.close();
    },
    true
  );

  /* L'état ouvert/fermé se lit sur le <dialog> ; la barre, elle, en a besoin
     pour basculer l'icône. Un MutationObserver sur l'attribut `open` couvre
     les deux sens (clic, Échap, clic sur le fond) sans dupliquer la logique
     de fermeture de ms-modale. */
  document.querySelectorAll('[data-ms-en-tete-fermer-tiroir]').forEach((bouton) => {
    const boite = document.getElementById(bouton.dataset.msEnTeteFermerTiroir);
    if (!boite) return;
    const libelle = bouton.querySelector('[data-ms-en-tete-libelle-burger]');
    const libelleOuvrir = libelle ? libelle.textContent : '';

    const refleter = () => {
      bouton.classList.toggle('ms-en-tete__burger--ouvert', boite.open);
      bouton.setAttribute('aria-expanded', String(boite.open));
      if (libelle) libelle.textContent = boite.open ? libelle.dataset.fermer || 'Fermer le menu' : libelleOuvrir;
    };
    refleter();
    new MutationObserver(refleter).observe(boite, { attributes: true, attributeFilter: ['open'] });
  });

  const majCompteurs = (compte) => {
    document.querySelectorAll('[data-ms-compteur-panier]').forEach((pastille) => {
      pastille.textContent = compte;
      pastille.hidden = compte === 0;
    });
  };

  document.addEventListener('ms:panier:maj', (evenement) => {
    const panier = evenement.detail && evenement.detail.cart;
    if (panier && typeof panier.item_count === 'number') majCompteurs(panier.item_count);
  });

  window.addEventListener('pageshow', (evenement) => {
    if (!evenement.persisted) return;
    const pastille = document.querySelector('[data-ms-compteur-panier]');
    if (!pastille) return;
    fetch(pastille.dataset.url)
      .then((reponse) => reponse.json())
      .then((panier) => majCompteurs(panier.item_count))
      .catch(() => {});
  });
}
